import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, ArrowLeft, Zap, Shield, Truck, ChevronRight, Wind } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { productData } from '../mock/productData';

const ProductPage = () => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);

  const handleBuyNow = () => {
    navigate('/checkout', {
      state: {
        product: {
          name: productData.name,
          price: productData.price,
          image: productData.images[0]
        }
      }
    });
  };

  const images = productData.images;
  const price = productData.price;
  const comparePrice = 3049;
  const discount = Math.round(((comparePrice - price) / comparePrice) * 100);

  return (
    <div className="min-h-screen bg-dark-primary">
      {/* Navigation */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 bg-dark-primary/80 backdrop-blur-lg border-b border-gold/10"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:scale-105 transition-transform">
            <Wind className="w-8 h-8 text-gold" />
            <span className="text-2xl font-bold text-gold">MADTOYS</span>
          </Link>
          <Button onClick={handleBuyNow} className="bg-gold hover:bg-gold-dark text-dark-primary font-semibold">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Buy Now
          </Button>
        </div>
      </motion.nav>

      {/* Product Content */}
      <div className="container mx-auto px-6 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-gray-400 hover:text-gold mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Product Images */}
            <div className="space-y-6">
              <motion.div 
                className="relative overflow-hidden rounded-2xl border-2 border-gold/20 bg-dark-secondary"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src={images[selectedImage]} 
                  alt={productData.name}
                  className="w-full h-auto object-cover"
                  style={{ filter: 'drop-shadow(0 0 40px rgba(212, 175, 55, 0.3))' }}
                />
              </motion.div>
              
              {images.length > 1 && (
                <div className="grid grid-cols-5 gap-4">
                  {images.map((img, index) => (
                    <motion.button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-gold' : 'border-gray-700'
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <img src={img} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                    </motion.button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <Badge className="bg-gold/20 text-gold border-gold/30 mb-4">
                  Limited Edition
                </Badge>
                <motion.h1 
                  className="text-5xl font-bold text-white mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  {productData.name}
                </motion.h1>
                <motion.div
                  className="flex items-baseline gap-4 mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <span className="text-5xl font-bold text-gold">
                    ₹{Math.round(price).toLocaleString('en-IN')}
                  </span>
                  {comparePrice && (
                    <>
                      <span className="text-2xl text-gray-500 line-through">
                        ₹{Math.round(comparePrice).toLocaleString('en-IN')}
                      </span>
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        {discount}% OFF
                      </Badge>
                    </>
                  )}
                </motion.div>
              </div>

              <motion.div
                className="prose prose-invert max-w-none"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <p className="text-gray-300 leading-relaxed text-lg">
                  {productData.description}
                </p>
              </motion.div>

              {/* Trust Badges */}
              <motion.div
                className="grid grid-cols-3 gap-4 py-8 border-y border-gold/10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="text-center">
                  <Truck className="w-8 h-8 text-gold mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Free Shipping</p>
                </div>
                <div className="text-center">
                  <Shield className="w-8 h-8 text-gold mx-auto mb-2" />
                  <p className="text-sm text-gray-400">7-Day Returns</p>
                </div>
                <div className="text-center">
                  <Zap className="w-8 h-8 text-gold mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Fast Delivery</p>
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                className="space-y-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Button 
                  onClick={handleBuyNow}
                  size="lg" 
                  className="w-full bg-gold hover:bg-gold-dark text-dark-primary font-bold text-lg py-8"
                >
                  Buy Now - Secure Checkout
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-center text-gray-500 text-sm">
                  Secure payment powered by Razorpay
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductPage;
